import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import { Progress } from '@/components/ui/progress'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { adminApi } from '@/lib/api'
import { Upload, Link, AlertCircle, CheckCircle, ChevronDown, ChevronRight } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'

interface ImportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  kbId: string
  kbName: string
  fields: Array<{
    name: string
    pgType: string
    fieldType?: string
  }>
  onImportComplete: () => void
}

export function ImportDialog({
  open,
  onOpenChange,
  kbId,
  kbName,
  fields,
  onImportComplete
}: ImportDialogProps) {
  const { toast } = useToast()
  const [importType, setImportType] = useState<'file' | 'url'>('file')
  const [file, setFile] = useState<File | null>(null)
  const [url, setUrl] = useState('')
  const [format, setFormat] = useState<'csv' | 'json'>('csv')
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [imageField, setImageField] = useState('')
  const [importing, setImporting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [importResult, setImportResult] = useState<any>(null)
  const [validating, setValidating] = useState(false)
  const [validationResult, setValidationResult] = useState<any>(null)

  // Find image fields
  const imageFields = fields.filter(f => f.fieldType === 'image' || f.pgType === 'IMAGE')

  // Reset all state when dialog closes
  const resetDialogState = () => {
    setImportType('file')
    setFile(null)
    setUrl('')
    setFormat('csv')
    setMapping({})
    setImageField('')
    setImporting(false)
    setProgress(0)
    setImportResult(null)
    setValidating(false)
    setValidationResult(null)
  }

  // Handle dialog close
  const handleClose = (open: boolean) => {
    if (!open) {
      resetDialogState()
    }
    onOpenChange(open)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setFile(selectedFile)
      // Auto-detect format from extension
      const ext = selectedFile.name.toLowerCase()
      if (ext.endsWith('.json')) {
        setFormat('json')
      } else if (ext.endsWith('.csv')) {
        setFormat('csv')
      }
    }
  }

  const handleValidate = async () => {
    try {
      setValidating(true)
      
      const validationData: any = {
        source: importType,
        format,
        mapping: Object.keys(mapping).length > 0 ? mapping : undefined,
        imageField: (imageField && imageField !== 'none') ? imageField : undefined
      }

      if (importType === 'url') {
        validationData.url = url
      }

      const result = await adminApi.validateImport(kbId, validationData)
      setValidationResult(result)
      
      if (!result.valid) {
        toast({
          title: 'Validation Issues',
          description: `Found ${result.issues?.length || 0} issue(s) with the import data`,
          variant: 'destructive'
        })
      }
    } catch (error) {
      console.error('Validation failed:', error)
      toast({
        title: 'Validation Failed',
        description: 'Failed to validate import data',
        variant: 'destructive'
      })
    } finally {
      setValidating(false)
    }
  }

  const handleImport = async () => {
    try {
      setImporting(true)
      setProgress(0)
      
      let result
      
      if (importType === 'file' && file) {
        const formData = new FormData()
        formData.append('file', file)
        if (Object.keys(mapping).length > 0) {
          formData.append('mapping', JSON.stringify(mapping))
        }
        if (imageField && imageField !== 'none') {
          formData.append('imageField', imageField)
        }
        
        result = await adminApi.importKnowledgeBaseFile(kbId, formData)
      } else if (importType === 'url') {
        result = await adminApi.importKnowledgeBaseUrl(kbId, {
          url,
          format,
          mapping: Object.keys(mapping).length > 0 ? mapping : undefined,
          imageField: (imageField && imageField !== 'none') ? imageField : undefined
        })
      }

      if (result) {
        setImportResult(result)
        
        if (result.success) {
          toast({
            title: 'Import Successful',
            description: `Imported ${result.imported} of ${result.total} documents`,
          })
          onImportComplete()
          // Close dialog after successful import
          setTimeout(() => handleClose(false), 2000)
        } else {
          toast({
            title: 'Import Completed with Errors',
            description: `Imported ${result.imported} of ${result.total} documents. ${result.failed} failed.`,
            variant: 'destructive'
          })
        }
      }
    } catch (error) {
      console.error('Import failed:', error)
      toast({
        title: 'Import Failed',
        description: 'Failed to import data',
        variant: 'destructive'
      })
    } finally {
      setImporting(false)
    }
  }

  const renderMappingFields = () => {
    if (format === 'json') {
      return (
        <div className="space-y-2">
          <Label>Field Mapping (Optional)</Label>
          <Textarea
            placeholder='{"image_filename": "image_url", "old_field": "new_field"}'
            value={JSON.stringify(mapping, null, 2)}
            onChange={(e) => {
              try {
                setMapping(JSON.parse(e.target.value))
              } catch {
                // Invalid JSON, ignore
              }
            }}
            rows={4}
          />
          <p className="text-sm text-muted-foreground">
            Map source fields to KB fields if names don't match. For example, map "image_filename" to "image_url".
          </p>
        </div>
      )
    }

    return null
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import Data to {kbName}</DialogTitle>
          <DialogDescription>
            Import documents from a CSV or JSON file, or from a URL
          </DialogDescription>
        </DialogHeader>

        <Tabs value={importType} onValueChange={(v) => setImportType(v as 'file' | 'url')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="file">
              <Upload className="h-4 w-4 mr-2" />
              Upload File
            </TabsTrigger>
            <TabsTrigger value="url">
              <Link className="h-4 w-4 mr-2" />
              Import from URL
            </TabsTrigger>
          </TabsList>

          <TabsContent value="file" className="space-y-4">
            <div>
              <Label htmlFor="file">Select File</Label>
              <Input
                id="file"
                type="file"
                accept=".json,.csv"
                onChange={handleFileChange}
                disabled={importing}
              />
            </div>
          </TabsContent>

          <TabsContent value="url" className="space-y-4">
            <div>
              <Label htmlFor="url">Data URL</Label>
              <Input
                id="url"
                type="url"
                placeholder="https://example.com/data.json"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={importing}
              />
            </div>
            <div>
              <Label htmlFor="format">Format</Label>
              <Select value={format} onValueChange={(v) => setFormat(v as 'csv' | 'json')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="json">JSON</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </TabsContent>
        </Tabs>

        <div className="space-y-4">
          {renderMappingFields()}

          {imageFields.length > 0 && (
            <div>
              <Label htmlFor="imageField">Image Field</Label>
              <Select value={imageField} onValueChange={setImageField}>
                <SelectTrigger>
                  <SelectValue placeholder="Select image field (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {imageFields.map(field => (
                    <SelectItem key={field.name} value={field.name}>
                      {field.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground mt-1">
                Select which field contains image URLs or filenames
              </p>
            </div>
          )}
        </div>

        {validationResult && (
          <Alert variant={validationResult.valid ? 'default' : 'destructive'}>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {validationResult.valid ? (
                <span>Validation passed. Ready to import {validationResult.preview.length} documents.</span>
              ) : (
                <div>
                  <p>Validation issues found:</p>
                  <ul className="list-disc list-inside mt-2">
                    {validationResult.issues.slice(0, 5).map((issue: string, i: number) => (
                      <li key={i} className="text-sm">{issue}</li>
                    ))}
                  </ul>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        {importing && (
          <div className="space-y-2">
            <Progress value={progress} />
            <p className="text-sm text-center text-muted-foreground">
              Importing documents...
            </p>
          </div>
        )}

        {importResult && (
          <Alert variant={importResult.success ? 'default' : 'destructive'}>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p>Import completed in {(importResult.duration / 1000).toFixed(1)}s</p>
                <p>Successfully imported: {importResult.imported} / {importResult.total}</p>
                {importResult.warnings && importResult.warnings.length > 0 && (
                  <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Warnings:</p>
                    <ul className="mt-1 list-disc list-inside text-sm text-yellow-700 dark:text-yellow-300">
                      {importResult.warnings.map((warning: string, i: number) => (
                        <li key={i}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {importResult.failed > 0 && (
                  <>
                    <p className="font-semibold">Failed: {importResult.failed}</p>
                    {importResult.errors && importResult.errors.length > 0 && (
                      <div className="mt-2">
                        <ErrorsList errors={importResult.errors} />
                      </div>
                    )}
                  </>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={importing}
          >
            Cancel
          </Button>
          {!importResult && (
            <>
              <Button
                variant="outline"
                onClick={handleValidate}
                disabled={
                  importing || 
                  validating ||
                  (importType === 'file' && !file) ||
                  (importType === 'url' && !url)
                }
              >
                Validate
              </Button>
              <Button
                onClick={handleImport}
                disabled={
                  importing ||
                  (importType === 'file' && !file) ||
                  (importType === 'url' && !url)
                }
              >
                Import
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Error list component with collapsible groups
function ErrorsList({ errors }: { errors: any[] }) {
  const [expandedErrors, setExpandedErrors] = useState<Set<number>>(new Set())
  
  // Group errors by type
  const errorGroups = errors.reduce((groups: Record<string, any[]>, error, index) => {
    const key = error.message.split(':')[0] || 'Other errors'
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push({ ...error, index })
    return groups
  }, {})
  
  const toggleError = (index: number) => {
    const newExpanded = new Set(expandedErrors)
    if (newExpanded.has(index)) {
      newExpanded.delete(index)
    } else {
      newExpanded.add(index)
    }
    setExpandedErrors(newExpanded)
  }
  
  return (
    <div className="space-y-3 max-h-[300px] overflow-y-auto">
      {Object.entries(errorGroups).map(([groupName, groupErrors]) => (
        <div key={groupName} className="border rounded-lg p-2">
          <p className="text-sm font-medium mb-2">
            {groupName} ({groupErrors.length})
          </p>
          <div className="space-y-2">
            {groupErrors.slice(0, 3).map((error: any) => (
              <Collapsible
                key={error.index}
                open={expandedErrors.has(error.index)}
                onOpenChange={() => toggleError(error.index)}
              >
                <div className="text-sm p-2 bg-destructive/10 rounded">
                  <CollapsibleTrigger className="flex items-center justify-between w-full text-left">
                    <p className="font-medium flex-1 pr-2 line-clamp-1">
                      {error.message}
                    </p>
                    {expandedErrors.has(error.index) ? (
                      <ChevronDown className="h-4 w-4 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 flex-shrink-0" />
                    )}
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    {error.suggestions && error.suggestions.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs font-medium">Suggestions:</p>
                        <ul className="list-disc list-inside text-xs space-y-1">
                          {error.suggestions.map((suggestion: string, j: number) => (
                            <li key={j} className="text-muted-foreground">
                              {suggestion}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {error.document && (
                      <details className="mt-2">
                        <summary className="text-xs cursor-pointer text-muted-foreground">
                          View document data
                        </summary>
                        <pre className="mt-1 text-xs overflow-x-auto p-2 bg-muted rounded">
                          {JSON.stringify(error.document, null, 2)}
                        </pre>
                      </details>
                    )}
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))}
            {groupErrors.length > 3 && (
              <p className="text-xs text-muted-foreground pl-2">
                ... and {groupErrors.length - 3} more similar errors
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}